import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbColorHelper,
  NbDateService,
  NbDatepickerModule,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbThemeService,
  NbToastrService,
  NbTooltipModule
} from '@nebular/theme';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { Store } from '@ngxs/store';
import { NgChartsModule } from 'ng2-charts';
import { Observable, Subject, takeUntil } from 'rxjs';
import { UtilityService } from 'src/app/core/services/utility.service';
import { Campaign } from 'src/app/models/campaign';
import { toYYYYMMDDInt } from 'src/app/shared/utils/date-formatter';
import { SharedModule } from '../../../../shared/shared.module';
import { LoadCampaigns, SaveCampaignDeliveries } from '../../redux/campaign.actions';
import { CampaignState } from '../../redux/campaign.state';
import { CampaignService } from '../../services/campaign.service';

@Component({
  selector: 'app-campaign-deliveries',
  templateUrl: './campaign-deliveries.component.html',
  styleUrls: ['./campaign-deliveries.component.css'],
  imports: [
    NbCardModule,
    NbIconModule,
    NbButtonModule,
    NbTooltipModule,
    NbFormFieldModule,
    NbInputModule,
    NbDatepickerModule,
    FormsModule,
    NgSelectComponent,
    NgFor,
    NgOptionComponent,
    NgIf,
    SharedModule,
    NgChartsModule,
    AsyncPipe
  ]
})
export class CampaignDeliveriesComponent implements OnInit, OnDestroy {
  public campaign$: Observable<Campaign[] | undefined> = inject(Store).select(CampaignState.campaign);
  public delivery$: Observable<any[]> = inject(Store).select(CampaignState.deliveries);
  private unsubscribe = new Subject<void>();

  public formatDate = 'YYYYMMDD';

  //Using to disable future dates in datepicker
  public max: Date;
  public showSpinner = false;
  public refreshButton = true;

  public currentTime: number = new Date().getTime();
  public weeksTimeLimit?: number;
  public weeksTime_string?: string;

  public selectedCampaignID?: string;
  public isSelected = false;
  public count = 0;

  //This is only used for date picker to store and show picked date
  public start: any;
  public end: any;

  //This hold array of dates that we calculate in calculateDate function
  public chartData: { data?: { delivery_date: string; count: number }[]; dates: string[]; count: number[] } = {
    data: undefined,
    dates: [],
    count: []
  };

  public data: any;
  public options: any;
  public themeSubscription: any;

  constructor(
    private theme: NbThemeService,
    public utilityService: UtilityService,
    private store: Store,
    private service: CampaignService,
    private toastrService: NbToastrService,
    protected dateService: NbDateService<Date>
  ) {
    //Nebular service calculate current date to disable datepicker future dates
    this.max = this.dateService.addMonth(this.dateService.today(), 0);

    if (!this.store.selectSnapshot(CampaignState.campaign)) {
      this.store.dispatch(LoadCampaigns);
    }
  }
  public ngOnInit(): void {
    this.weeksTimeLimit = this.currentTime / 1000 - 604800;
    this.weeksTime_string = this.utilityService.convertUnixTime_global(this.weeksTimeLimit);
  }

  public ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public async selected() {
    if (this.selectedCampaignID) {
      this.refreshButton = false;
      let found: any;
      this.delivery$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
        found = data.find((element) => element.campaign_id === this.selectedCampaignID);
      });
      if (found) {
        if (this.start && this.end) {
          if (
            found.data.some(
              (e: any) =>
                e.delivery_date === toYYYYMMDDInt(this.start) &&
                found.data.some((e: any) => e.delivery_date === toYYYYMMDDInt(this.end))
            )
          ) {
            this.chartData.data = found.data;
            this.prepareData();
          } else {
            this.fetchFromApi();
          }
        } else {
          this.chartData.data = found.data;
          this.prepareData();
        }
      } else {
        this.fetchFromApi();
      }
    } else {
      this.start = null;
      this.end = null;
      this.isSelected = false;
      this.refreshButton = true;
    }
  }

  public async fetchFromApi() {
    if (this.selectedCampaignID === undefined) {
      return;
    }

    this.showSpinner = true;
    let dateStart;
    let dateEnd;

    if (this.start && this.end) {
      dateStart = toYYYYMMDDInt(this.start);
      dateEnd = toYYYYMMDDInt(this.end);
    } else {
      return;
    }

    const response = await this.service.getCampaignDeliveries(this.selectedCampaignID, dateStart, dateEnd);
    this.chartData.data = response[this.selectedCampaignID];
    this.toastrService.show(status, $localize`Fetch: Complete`, { status: 'success' });

    if (this.chartData.data) {
      const object = {
        data: this.chartData.data,
        campaign_id: this.selectedCampaignID
      };
      this.store.dispatch(new SaveCampaignDeliveries(object));
    }
    this.showSpinner = false;
    this.prepareData();
  }

  public prepareData() {
    this.chartData.dates = [];
    this.chartData.count = [];
    this.count = 0;

    if (this.chartData.data === undefined) {
      return;
    }

    this.chartData.data.forEach((element: { delivery_date: string; count: number }) => {
      const formatedDate = element.delivery_date.toString().replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3');
      this.chartData.dates.push(formatedDate);
      this.chartData.count.push(element.count);
      this.count = this.count + element.count;
    });

    this.isSelected = true;
    this.createCharts();
  }

  public createCharts() {
    this.themeSubscription = this.theme
      .getJsTheme()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((config) => {
        const colors: any = config.variables;
        this.data = {
          labels: this.chartData.dates,
          datasets: [
            {
              data: this.chartData.count,
              label: $localize`Number of deliveries`,
              backgroundColor: NbColorHelper.hexToRgbA(colors.primaryLight, 0.8)
            }
          ]
        };
        this.options = {
          maintainAspectRatio: false,
          responsive: true,
          scales: {
            y: {
              grid: {
                display: false
              }
            },
            x: {
              grid: {
                display: false
              }
            }
          }
        };
      });
  }

  public startDate(date: Date) {
    this.start = date;
  }

  public endDate(date: Date) {
    this.end = date;
  }

  public refresh() {
    this.refreshButton = true;
    this.fetchFromApi();

    setTimeout(() => {
      this.refreshButton = false;
    }, 30000);
  }
}
